import type { ChartData } from 'chart.js';
import { useMemo } from 'react';

import {
  CHART_METRIC_VIEW_TYPE,
  CHART_TOOLTIP_TARGET_TYPE,
} from '@/entities/chart/model/constants';
import useChartColor from '@/features/(charts)/chart-renderer/hooks/use-chart-color';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { getTimezoneDayjs } from '@/utils/time';
import { getPercentStringFormat } from '@/utils/util';

type Props = {
  chart: ChartBase;
  tableData: ChartTableRes;
};

const useChartTimeSeriesStackedBarData = (props: Props) => {
  const { chart, tableData } = props;
  if (!chart.historical.frequency) throw new Error('Frequency must be set');
  const { target, field } = chart;
  const { term } = target;
  const getLabel = useChartLabel(tableData.field_item_meta);
  const getColor = useChartColor(field);
  const crossTableData: ChartCrossTableRes = chartUtils.getSortedRowItems(
    chart,
    tableData as ChartCrossTableRes,
    chart.selected_field.items || [],
  ) as ChartCrossTableRes;

  const formatter = chartUtils.getFormatByFrequency(chart.historical.frequency);

  const dateLabels = chartUtils.getDateLabelsByUnit({
    unit: term.unit,
    dateRange: chartUtils.getTimeRangeByTerm(getTimezoneDayjs(), term),
  });

  const compareDateRange = chartUtils.getCompareTimeRangeByTerm(
    getTimezoneDayjs(),
    term,
    chart.target.compare_term,
  );

  const compareDateLabels = chartUtils.getDateLabelsByUnit({
    unit: term.unit,
    dateRange: compareDateRange,
  });

  const timeSeriesChartData = useMemo(() => {
    let result: ChartData<'bar'> = {
      labels: dateLabels,
      datasets: [],
    };
    const totalMap = chartUtils.getKeyValueMapByKeys(dateLabels);

    crossTableData.tables[0].forEach((x) => {
      x.values.find((y) => {
        const formattedDate = formatter(getTimezoneDayjs(y.y.toString()));
        totalMap[formattedDate] += y.value;
      });
    });

    const datasets: ChartData<'bar'>['datasets'] =
      crossTableData.tables[0]?.map((candidate) => {
        const dateMap = chartUtils.getKeyValueMapByKeys(dateLabels);

        candidate.values?.forEach((y) => {
          const formattedDate = formatter(getTimezoneDayjs(y.y.toString()));
          const percentage = Number(
            getPercentStringFormat(totalMap[formattedDate], y.value, 2),
          );
          const count = y.value;
          if (
            chart.series[0]?.options?.view_type ===
            CHART_METRIC_VIEW_TYPE.PERCENTAGE
          ) {
            dateMap[formattedDate] = percentage;
          } else {
            dateMap[formattedDate] = count;
          }
        });
        return {
          label: getLabel(field.type, candidate.x),
          data: Object.values(dateMap),
          backgroundColor: getColor(candidate.x),
          stack: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
          date_type: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
          meta: {
            x: candidate.x,
            y: dateLabels,
          },
        };
      }) || [];

    result.datasets.push(...datasets);

    if (chart.target.is_compare) {
      const compareTotalMap =
        chartUtils.getKeyValueMapByKeys(compareDateLabels);

      crossTableData.compare_tables?.[0]?.forEach((x) => {
        x.values.find((y) => {
          const formattedDate = formatter(getTimezoneDayjs(y.y.toString()));
          compareTotalMap[formattedDate] += y.value;
        });
      });

      const compareDatasets: ChartData<'bar'>['datasets'] =
        crossTableData.compare_tables?.[0]?.map((item) => {
          const compareDateMap =
            chartUtils.getKeyValueMapByKeys(compareDateLabels);

          item?.values?.forEach((y) => {
            const formattedDate = formatter(getTimezoneDayjs(y.y.toString()));
            const percentage = Number(
              getPercentStringFormat(
                compareTotalMap[formattedDate],
                y.value,
                2,
              ),
            );
            const count = y.value;
            if (
              chart.series[0]?.options?.view_type ===
              CHART_METRIC_VIEW_TYPE.PERCENTAGE
            ) {
              compareDateMap[formattedDate] = percentage;
            } else {
              compareDateMap[formattedDate] = count;
            }
          });

          return {
            label: getLabel(field.type, item.x),
            data: Object.values(compareDateMap),
            backgroundColor: getAlphaColor(getColor(item.x), 0.6),
            stack: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
            date_type: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
            meta: {
              x: item.x,
              y: compareDateLabels,
            },
          };
        }) || [];

      result.datasets.unshift(...compareDatasets);
    }

    return result;
  }, [
    chart.series,
    chart.target.is_compare,
    compareDateLabels,
    crossTableData.compare_tables,
    crossTableData.tables,
    dateLabels,
    field.type,
    formatter,
    getColor,
    getLabel,
  ]);

  return timeSeriesChartData;
};

export default useChartTimeSeriesStackedBarData;
